import type React from "react";
import type { Layout } from "react-resizable-panels";

import { Trans } from "@lingui/react/macro";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { useEffect, useRef, useState } from "react";
import { usePanelRef } from "react-resizable-panels";

import { LoadingScreen } from "@/components/layout/loading-screen";
import { useCSSVariables } from "@/components/resume/hooks/use-css-variables";
import { setResumeSyncCacheInvalidator, useResumeStore } from "@/components/resume/store/resume";
import { Button } from "@/components/ui/button";
import { ResizableGroup, ResizablePanel, ResizableSeparator } from "@/components/ui/resizable";
import { useIsMobile } from "@/hooks/use-mobile";
import { isCoreV2ProductLine } from "@/integrations/multiresume/core/product-line";
import { orpc } from "@/integrations/orpc/client";
import { multiResumeBrand } from "@/utils/multiresume-brand";

import { FinalValidationExportProvider } from "./-components/export-preflight-warning";
import { BuilderHeader } from "./-components/header";
import { BuilderSidebarLeft } from "./-sidebar/left";
import { BuilderSidebarRight } from "./-sidebar/right";
import { JdGuidedWorkflow } from "./-sidebar/right/sections/jd-guided-workflow";
import {
  BUILDER_LAYOUT_COOKIE_NAME,
  DEFAULT_BUILDER_LAYOUT,
  mapPanelLayoutToBuilderLayout,
  parseBuilderLayoutCookie,
  useBuilderSidebar,
  useBuilderSidebarStore,
  type BuilderLayout,
} from "./-store/sidebar";

export const Route = createFileRoute("/builder/$resumeId")({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    if (!context.session) throw redirect({ to: "/auth/login", replace: true });
    return { session: context.session };
  },
  loader: async ({ params, context }) => {
    const [layout, resume] = await Promise.all([
      getBuilderLayoutServerFn(),
      context.queryClient.ensureQueryData(orpc.resume.getById.queryOptions({ input: { id: params.resumeId } })),
    ]);

    return { layout, name: resume.name };
  },
  head: ({ loaderData }) => ({
    meta: loaderData ? [{ title: `${loaderData.name} - ${multiResumeBrand.appName}` }] : undefined,
  }),
});

function RouteComponent() {
  const { layout: initialLayout } = Route.useLoaderData();

  const { resumeId } = Route.useParams();
  const { data: resume } = useSuspenseQuery(orpc.resume.getById.queryOptions({ input: { id: resumeId } }));
  const queryClient = useQueryClient();

  const style = useCSSVariables(resume.data);
  const isReady = useResumeStore((state) => state.isReady);
  const initialize = useResumeStore((state) => state.initialize);

  useEffect(() => {
    initialize(resume);
    return () => initialize(null);
  }, [resume, initialize]);

  useEffect(
    () =>
      setResumeSyncCacheInvalidator(async (resumeId, options) => {
        const refetchType = options.refetch === true ? "all" : "none";

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: orpc.resume.getById.queryKey({ input: { id: resumeId } }),
            refetchType,
          }),
          queryClient.invalidateQueries({
            queryKey: orpc.multiResumeWorkspace.editorContext.queryKey({ input: { resumeId } }),
            refetchType,
          }),
          queryClient.invalidateQueries({
            queryKey: orpc.multiResumeWorkspace.state.queryKey(),
            refetchType,
          }),
        ]);
      }),
    [queryClient],
  );

  if (!isReady) return <LoadingScreen />;

  return <BuilderLayout style={style} initialLayout={initialLayout} />;
}

type BuilderLayoutProps = React.ComponentProps<"div"> & {
  initialLayout: BuilderLayout;
};

function BuilderLayout({ initialLayout, ...props }: BuilderLayoutProps) {
  const coreV2 = isCoreV2ProductLine();
  const [jdUnderstandingActive, setJdUnderstandingActive] = useState(
    () => coreV2 && window.location.hash === "#jd-understanding",
  );

  useEffect(() => {
    const sync = () => setJdUnderstandingActive(coreV2 && window.location.hash === "#jd-understanding");
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [coreV2]);

  const leaveWorkbench = (nextHash = "") => {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}${nextHash ? `#${nextHash}` : ""}`,
    );
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  };

  if (jdUnderstandingActive) {
    return <JdUnderstandingWorkbench {...props} onLeave={leaveWorkbench} />;
  }

  return <NormalBuilderLayout {...props} initialLayout={initialLayout} />;
}

function NormalBuilderLayout({ initialLayout, ...props }: BuilderLayoutProps) {
  const isMobile = useIsMobile();
  const canPersistLayoutRef = useRef(false);

  const leftSidebarRef = usePanelRef();
  const rightSidebarRef = usePanelRef();

  const setLeftSidebar = useBuilderSidebarStore((state) => state.setLeftSidebar);
  const setRightSidebar = useBuilderSidebarStore((state) => state.setRightSidebar);
  const setLayout = useBuilderSidebarStore((state) => state.setLayout);

  const { maxSidebarSize, collapsedSidebarSize } = useBuilderSidebar((state) => ({
    maxSidebarSize: state.maxSidebarSize,
    collapsedSidebarSize: state.collapsedSidebarSize,
  }));

  useEffect(() => {
    setLayout(initialLayout);
    canPersistLayoutRef.current = true;
  }, [initialLayout, setLayout]);

  const onLayoutChanged = (layout: Layout) => {
    const nextLayout = mapPanelLayoutToBuilderLayout(layout);
    if (!canPersistLayoutRef.current) return;
    setLayout(nextLayout);
    void setBuilderLayoutServerFn({ data: nextLayout });
  };

  useEffect(() => {
    if (!leftSidebarRef || !rightSidebarRef) return;

    setLeftSidebar(leftSidebarRef);
    setRightSidebar(rightSidebarRef);
  }, [leftSidebarRef, rightSidebarRef, setLeftSidebar, setRightSidebar]);

  const sidebarMinSize = isMobile ? "0%" : `${collapsedSidebarSize * 2}px`;
  const sidebarCollapsedSize = isMobile ? "0%" : `${collapsedSidebarSize}px`;
  const leftSidebarSize = isMobile ? "0%" : `${initialLayout.left}%`;
  const rightSidebarSize = isMobile ? "0%" : `${initialLayout.right}%`;
  const artboardSize = isMobile ? "100%" : `${initialLayout.artboard}%`;

  return (
    <FinalValidationExportProvider>
      <div className="flex h-svh flex-col" {...props}>
        <BuilderHeader />

        <ResizableGroup orientation="horizontal" className="mt-14 flex-1" onLayoutChanged={onLayoutChanged}>
          <ResizablePanel
            collapsible
            id="left"
            panelRef={leftSidebarRef}
            maxSize={maxSidebarSize}
            minSize={sidebarMinSize}
            collapsedSize={sidebarCollapsedSize}
            defaultSize={leftSidebarSize}
            className="z-20 h-[calc(100svh-3.5rem)]"
          >
            <BuilderSidebarLeft />
          </ResizablePanel>
          <ResizableSeparator withHandle className="z-50 border-s" />
          <ResizablePanel id="artboard" defaultSize={artboardSize} className="h-[calc(100svh-3.5rem)]">
            <Outlet />
          </ResizablePanel>
          <ResizableSeparator withHandle className="z-50 border-e" />
          <ResizablePanel
            collapsible
            id="right"
            panelRef={rightSidebarRef}
            maxSize={maxSidebarSize}
            minSize={sidebarMinSize}
            collapsedSize={sidebarCollapsedSize}
            defaultSize={rightSidebarSize}
            className="z-20 h-[calc(100svh-3.5rem)]"
          >
            <BuilderSidebarRight />
          </ResizablePanel>
        </ResizableGroup>
      </div>
    </FinalValidationExportProvider>
  );
}

/**
 * JD Understanding is a dedicated workbench. Guided Review remains in the
 * right-panel queue, but understanding deliberately owns the whole builder
 * surface so its source → reasoning → strategy order is not compressed.
 */
function JdUnderstandingWorkbench({
  onLeave,
  ...props
}: React.ComponentProps<"div"> & { onLeave: (nextHash?: string) => void }) {
  const { resumeId } = Route.useParams();

  return (
    <div className="h-svh overflow-y-auto bg-background" {...props}>
      <main data-testid="jd-understanding-workbench" data-phase6-surface="full-width">
        <div className="mx-auto min-h-svh w-full max-w-6xl px-4 py-5 sm:px-8 lg:px-12">
          <div className="mb-5 flex items-center justify-between gap-3 border-b pb-4">
            <div>
              <p className="text-sm text-muted-foreground">
                <Trans>Job Version workspace</Trans>
              </p>
              <h1 className="text-xl font-semibold">
                <Trans>JD Understanding</Trans>
              </h1>
            </div>
            <Button type="button" variant="outline" onClick={() => onLeave()}>
              <ArrowLeftIcon />
              <Trans>Back to editor</Trans>
            </Button>
          </div>
          <JdGuidedWorkflow targetResumeId={resumeId} onGenerated={() => onLeave("guided-review")} />
        </div>
      </main>
    </div>
  );
}

const setBuilderLayoutServerFn = createServerFn({ method: "POST" })
  .inputValidator((data): BuilderLayout => parseBuilderLayoutCookie(JSON.stringify(data)))
  .handler(async ({ data }) => {
    setCookie(BUILDER_LAYOUT_COOKIE_NAME, JSON.stringify(data), { path: "/" });
  });

const getBuilderLayoutServerFn = createServerFn({ method: "GET" }).handler(async (): Promise<BuilderLayout> => {
  const layout = getCookie(BUILDER_LAYOUT_COOKIE_NAME);
  if (!layout) return DEFAULT_BUILDER_LAYOUT;
  return parseBuilderLayoutCookie(layout);
});
